import { faInfoCircle, faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip, Button } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';

import BinanceIcon from '../../../assets/icons/binance-icon.svg';
import CoincallIcon from '../../../assets/icons/coincall-icon.svg';
import BullishLightIcon from '../../../assets/icons/bullish-light-icon.svg';
import BybitIcon from '../../../assets/bybit-dark-logo.svg';
import DeribitIcon from '../../../assets/icons/deribit-icon.svg';
import OkxIcon from '../../../assets/icons/okx-icon.svg';
import OtcIcon from '../../../assets/icons/otc-icon.svg';
import { AnimatedTransition } from '../../../components/animated-transition';
import { BLUR_STYLE } from '../../../providers/demo-blur';
import { IAccount, PortfolioManagementContext } from '../../../providers/portfolio-management';
import './styles.scss';
import { IAccountOption } from '../../../providers/venue-provider';
import Utils from '../../../utils/immersive_utils';

type PortfolioHeaderProps = {
  isAggregatedAccounts?: boolean;
};

export const PortfolioHeader = ({ isAggregatedAccounts = false }: PortfolioHeaderProps) => {
  const [accountName, setAccountName] = useState<string>('');
  const [exchange, setExchange] = useState<string>('');
  const {
    getDataDetailsAccount,
    aggregatedData,
    selectedAccountOption,
    isNamesBlurred,
    setIsNamesBlurred,
  } = useContext(PortfolioManagementContext);
  const exchangeIcons: any = {
    deribit: DeribitIcon,
    okx: OkxIcon,
    otc: OtcIcon,
    binance: BinanceIcon,
    bybit: BybitIcon,
    coincall: CoincallIcon,
    bullish: BullishLightIcon,
  };
  const exchangeIcon = exchangeIcons[exchange];

  useEffect(() => {
    const detailsAccount: IAccount | null = getDataDetailsAccount(selectedAccountOption);

    if (detailsAccount) {
      setAccountName(detailsAccount.name);
      setExchange(detailsAccount.exchange);
    } else {
      const interval = setInterval(() => {
        const detailsAccount: IAccount | null = getDataDetailsAccount(selectedAccountOption);

        if (detailsAccount) {
          setAccountName(detailsAccount.name);
          setExchange(detailsAccount.exchange);

          if (detailsAccount.name && detailsAccount.exchange) {
            clearInterval(interval);
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [selectedAccountOption, getDataDetailsAccount]);

  const renderListAccountsAggregatedCalculated = useMemo(() => {
    const accountsAggregated = aggregatedData.accounts;

    const groupedAccounts = accountsAggregated.reduce((acc: any, account: any) => {
      const { exchange, name } = account;
      acc[exchange] = acc[exchange] || [];
      acc[exchange].push({ name });
      return acc;
    }, {});

    const renderAccounts = (exchangeParam: string) => (
      <>
        <div className="list-accounts-aggregated-exchange-label">
          <img src={exchangeParam === 'deribit' ? DeribitIcon : OkxIcon} alt={`icon ${exchange}`} />
          <span>
            {exchangeParam === 'deribit'
              ? Utils.capitalizeFirstLetter(exchangeParam)
              : exchangeParam.toUpperCase()}
          </span>
        </div>
        {groupedAccounts[exchangeParam]?.map((account: IAccountOption, index: number) => (
          <div key={`${account.name}-${index}`} className="list-accounts-aggregated-names">
            <span style={isNamesBlurred ? BLUR_STYLE : undefined}>{account.name}</span>
          </div>
        ))}
      </>
    );

    return (
      <div className="list-accounts-aggregated-calculated">
        <h2 className="list-accounts-aggregated-title">Accounts:</h2>
        {renderAccounts('deribit')}
        {renderAccounts('okx')}
      </div>
    );
  }, [exchange, aggregatedData]);

  return (
    <div className="portfolio-management-header">
      <AnimatedTransition className="fade">
        {!isAggregatedAccounts ? (
          accountName &&
          exchange && (
            <div className="portfolio-management-header-account-title">
              <img src={exchangeIcon} alt="account" />
              <h2 style={isNamesBlurred ? BLUR_STYLE : undefined}>{accountName}</h2>
            </div>
          )
        ) : (
          <div className="portfolio-management-header-accounts-aggregated">
            <h2>Aggregated accounts</h2>
            <div className="portfolio-management-header-accounts-aggregated-tooltip-area">
              <Tooltip title={renderListAccountsAggregatedCalculated}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </div>
          </div>
        )}
      </AnimatedTransition>
      <Tooltip title={isNamesBlurred ? 'Show account names' : 'Blur account names for demo'}>
        <Button
          className="portfolio-management-header-blur-toggle"
          aria-label={isNamesBlurred ? 'Show account names' : 'Blur account names for demo'}
          onClick={() => setIsNamesBlurred((prev) => !prev)}
        >
          <FontAwesomeIcon icon={isNamesBlurred ? faEyeSlash : faEye} />
        </Button>
      </Tooltip>
    </div>
  );
};
